import React, { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import {
  RefreshCw,
  Mail,
  Rocket,
  BarChart3,
  CheckCircle2,
  PieChart,
  ShieldCheck,
} from "lucide-react";
import {
  ResponsiveContainer,
  PieChart as RPieChart,
  Pie,
  Cell,
  Tooltip as RTooltip,
  BarChart as RBarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
} from "recharts";

import HeaderMainPage from "../../header/HeaderMainPage";

/**
 *   Outreach – Dashboard Page (Email + LinkedIn)
 * - Light, high-clarity layout (soft gradients, dark text)
 * - Supports both email_status_count and linkedin_status_count
 * - Supports dashboard API "stats" block (sent/opened/replied/interested + rates)
 * - Backward-compatible: if "stats" missing, it derives safe defaults
 * - Interested is COMMON for both Email + LinkedIn (visible for both channels)
 */

const roboman_api = process.env.REACT_APP_ROBOMAN_API;
const CONTRAST_KEY = "outreach_dash_contrast"; // "1" = High, "0" = Normal

const CHANNELS = Object.freeze({ EMAIL: "email", LINKEDIN: "linkedin" });

const EMAIL_STATUS_ORDER = [
  "not started",
  "waiting to review",
  "reviewed",
  "sent",
  "opened",
  "replied",
];

const LINKEDIN_STATUS_ORDER = [
  "not started",
  "sent",
  "opened",
  "replied",
  "responded",
];

const EMAIL_STATUS_COLORS = {
  "not started": "#94a3b8", // slate-400
  "waiting to review": "#f59e0b", // amber-500
  reviewed: "#6366f1", // indigo-500
  sent: "#3b82f6", // blue-500
  opened: "#8b5cf6", // violet-500
  replied: "#10b981", // emerald-500
};

const LINKEDIN_STATUS_COLORS = {
  "not started": "#94a3b8",
  sent: "#0A66C2", // LinkedIn blue
  opened: "#60A5FA", // lighter blue
  replied: "#10b981", // emerald
  responded: "#14b8a6", // teal
};

const MOCK_RESPONSE = {
  num_of_campaigns: 5,
  num_of_companies: 120,
  email_status_count: {
    "not started": 18,
    "waiting to review": 12,
    reviewed: 20,
    sent: 70,
    opened: 40,
    replied: 15,
  },
  linkedin_status_count: {
    "not started": 10,
    sent: 25,
    opened: 5,
    replied: 2,
    responded: 2,
  },
  stats: {
    prospects_count: 120,
    sent: { count: 70, rate: 70 / 120 },
    opened: { count: 40, rate: 40 / 70 },
    replied: { count: 15, rate: 15 / 70 },
    interested: { count: 6, rate: 6 / 120 },
  },
};

function getAuthToken() {
  const raw = window.localStorage.getItem("access_token");
  if (!raw) return "";
  return raw.startsWith("Bearer ") ? raw : `Bearer ${raw}`;
}

// 🔁 Dashboard API fetch
const fetchDashboard = async (access_token) => {
  try {
    const response = await fetch(`${roboman_api}/campaign/dashboard/`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: access_token,
      },
    });
    console.log("Dashboard API response status:", response.status);

    if (!response.ok) {
      console.error(`HTTP error! status: ${response.status}`);
      return null;
    }

    const data = await response.json();
    console.log("Fetched dashboard data:", data);
    return data;
  } catch (error) {
    console.error("Error fetching dashboard data:", error);
    return null;
  }
};

/**
 * ✅ Backward-compatible normalizer
 */
function normalizeDashboardData(raw) {
  const safe = raw && typeof raw === "object" ? raw : {};

  const num_of_campaigns =
    Number(safe.num_of_campaigns ?? safe.num_campaigns ?? 0) || 0;
  const num_of_companies =
    Number(safe.num_of_companies ?? safe.num_companies ?? 0) || 0;

  const email_status_count =
    safe.email_status_count && typeof safe.email_status_count === "object"
      ? safe.email_status_count
      : {};

  const linkedin_status_count =
    safe.linkedin_status_count && typeof safe.linkedin_status_count === "object"
      ? safe.linkedin_status_count
      : {};

  // If backend provides stats, normalize it
  if (safe.stats && typeof safe.stats === "object") {
    const s = safe.stats;

    const prospects = Number(s.prospects_count ?? num_of_companies ?? 0) || 0;

    const sentCount = Number(s?.sent?.count ?? email_status_count?.sent ?? 0) || 0;
    const openedCount =
      Number(s?.opened?.count ?? email_status_count?.opened ?? 0) || 0;
    const repliedCount =
      Number(s?.replied?.count ?? email_status_count?.replied ?? 0) || 0;

    const interestedCount =
      Number(s?.interested?.count ?? 0) || 0;

    const sentRate = Number.isFinite(s?.sent?.rate)
      ? Number(s.sent.rate)
      : prospects > 0
      ? sentCount / prospects
      : 0;

    const openedRate = Number.isFinite(s?.opened?.rate)
      ? Number(s.opened.rate)
      : sentCount > 0
      ? openedCount / sentCount
      : 0;

    const repliedRate = Number.isFinite(s?.replied?.rate)
      ? Number(s.replied.rate)
      : sentCount > 0
      ? repliedCount / sentCount
      : 0;

    const interestedRate = Number.isFinite(s?.interested?.rate)
      ? Number(s.interested.rate)
      : prospects > 0
      ? interestedCount / prospects
      : 0;

    return {
      num_of_campaigns,
      num_of_companies,
      email_status_count,
      linkedin_status_count,
      stats: {
        prospects_count: prospects,
        sent: { count: sentCount, rate: sentRate },
        opened: { count: openedCount, rate: openedRate },
        replied: { count: repliedCount, rate: repliedRate },
        interested: { count: interestedCount, rate: interestedRate },
      },
    };
  }

  // If stats missing (old API), derive a compatible stats block
  const prospects = num_of_companies;
  const sentCount = Number(email_status_count?.sent ?? 0) || 0;
  const openedCount = Number(email_status_count?.opened ?? 0) || 0;
  const repliedCount = Number(email_status_count?.replied ?? 0) || 0;

  return {
    num_of_campaigns,
    num_of_companies,
    email_status_count,
    linkedin_status_count,
    stats: {
      prospects_count: prospects,
      sent: { count: sentCount, rate: prospects > 0 ? sentCount / prospects : 0 },
      opened: { count: openedCount, rate: sentCount > 0 ? openedCount / sentCount : 0 },
      replied: { count: repliedCount, rate: sentCount > 0 ? repliedCount / sentCount : 0 },
      interested: { count: 0, rate: 0 }, // can't infer from old response
    },
  };
}

function StatCard({
  icon: Icon,
  label,
  value,
  hint,
  accent = "from-slate-50 via-white to-sky-50",
  hc = false,
  badge,
}) {
  const border = hc ? "border-slate-300" : "border-slate-200";
  const halo = hc ? "bg-indigo-50" : "bg-slate-50";
  const labelTone = hc ? "text-slate-900" : "text-slate-600";
  const hintTone = hc ? "text-slate-700" : "text-slate-500";
  const accentClass = hc ? `${accent} via-slate-50` : accent;

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35 }}
      className={`relative overflow-hidden rounded-2xl border ${border} bg-gradient-to-br ${accentClass} p-5 shadow-[0_18px_45px_-18px_rgba(15,23,42,0.25)] transition-transform duration-200 hover:-translate-y-0.5 hover:shadow-[0_22px_55px_-20px_rgba(15,23,42,0.35)]`}
    >
      <div className={`absolute -right-6 -top-6 h-24 w-24 rounded-full ${halo}`} />
      <div className="flex items-start gap-4">
        <div className={`rounded-xl ${halo} p-3`}>
          <Icon className="h-6 w-6 text-indigo-600" />
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <p className={`text-sm font-medium ${labelTone}`}>{label}</p>
            {badge ? (
              <span className="rounded-full bg-slate-900 px-2 py-0.5 text-[10px] font-semibold text-slate-50">
                {badge}
              </span>
            ) : null}
          </div>
          <p className="truncate text-2xl font-semibold tracking-tight text-slate-900">
            {value}
          </p>
          {hint ? <p className={`mt-1 text-xs ${hintTone}`}>{hint}</p> : null}
        </div>
      </div>
    </motion.div>
  );
}

function ProgressStat({ label, value = 0, hc = false }) {
  const safeValue = Number.isFinite(value) ? value : 0;
  const labelTone = hc ? "text-slate-800" : "text-slate-600";
  const valueTone = hc ? "text-slate-900" : "text-slate-700";

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between text-sm">
        <span className={labelTone}>{label}</span>
        <span className={`font-semibold ${valueTone}`}>
          {safeValue.toFixed(1)}%
        </span>
      </div>
      <div className="h-2 w-full rounded-full bg-slate-100">
        <div
          className="h-2 rounded-full bg-gradient-to-r from-emerald-500 to-emerald-600"
          style={{
            width: `${Math.min(100, Math.max(0, safeValue))}%`,
          }}
        />
      </div>
    </div>
  );
}

function Section({ title, icon: Icon, children, actions, hc = false }) {
  const border = hc ? "border-slate-300" : "border-slate-200";
  const surface = hc ? "bg-white" : "bg-slate-50";

  return (
    <div
      className={`flex h-full flex-col rounded-2xl border ${border} ${surface} p-5 shadow-[0_16px_40px_-20px_rgba(15,23,42,0.15)] transition-shadow duration-200 hover:shadow-[0_20px_55px_-24px_rgba(15,23,42,0.35)]`}
    >
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="rounded-lg bg-indigo-50 p-2">
            <Icon className="h-5 w-5 text-indigo-600" />
          </div>
          <h3 className="text-base font-semibold text-slate-900">{title}</h3>
        </div>
        <div className="flex items-center gap-2 text-xs text-slate-500">
          {actions}
        </div>
      </div>
      {children}
    </div>
  );
}

function Skeleton({ className = "" }) {
  return (
    <div className={`animate-pulse rounded-xl bg-slate-200/60 ${className}`} />
  );
}

// Keep this as a "custom hook" (name starts with use), but it doesn't itself use other hooks.
function useChannelMetrics(data, channel) {
  const email = data?.email_status_count || {};
  const li = data?.linkedin_status_count || {};

  const src = channel === CHANNELS.EMAIL ? email : li;
  const order =
    channel === CHANNELS.EMAIL ? EMAIL_STATUS_ORDER : LINKEDIN_STATUS_ORDER;
  const colors =
    channel === CHANNELS.EMAIL ? EMAIL_STATUS_COLORS : LINKEDIN_STATUS_COLORS;

  const list = order.map((k) => ({
    status: k,
    name: k,
    count: Number(src[k] || 0),
    color: colors[k],
  }));

  const sent = Number(src["sent"] || 0);
  const opened = Number(src["opened"] || 0);
  const replied = Number(src["replied"] || 0);
  const responded = Number(src["responded"] || 0);

  const total = list.reduce((a, b) => a + b.count, 0);

  const openRate =
    sent > 0 ? (opened / sent) * 100 : total ? (opened / total) * 100 : 0;

  const replyNumerator =
    channel === CHANNELS.EMAIL ? replied : replied + responded;

  const replyRate =
    sent > 0
      ? (replyNumerator / sent) * 100
      : total
      ? (replyNumerator / total) * 100
      : 0;

  return {
    list,
    totals: { total, sent, opened, replied, responded },
    openRate,
    replyRate,
  };
}

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [usingMock, setUsingMock] = useState(false);
  const [lastUpdated, setLastUpdated] = useState("");
  const [highContrast, setHighContrast] = useState(true); // default High
  const [channel, setChannel] = useState(CHANNELS.EMAIL);

  // Persist contrast preference
  useEffect(() => {
    const saved = window.localStorage.getItem(CONTRAST_KEY);
    if (saved !== null) setHighContrast(saved === "1");
  }, []);

  useEffect(() => {
    window.localStorage.setItem(CONTRAST_KEY, highContrast ? "1" : "0");
  }, [highContrast]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const token = getAuthToken();
      const freshRaw = await fetchDashboard(token);

      if (!freshRaw) {
        setData(normalizeDashboardData(MOCK_RESPONSE));
        setUsingMock(true);
        setError("Failed to load dashboard. Showing mock data.");
      } else {
        setData(normalizeDashboardData(freshRaw));
        setUsingMock(false);
      }
    } catch (e) {
      setData(normalizeDashboardData(MOCK_RESPONSE));
      setUsingMock(true);
      setError(
        `${e instanceof Error ? e.message : "Failed to load"}. Showing mock data.`
      );
    } finally {
      setLastUpdated(new Date().toLocaleString());
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const axisColor = highContrast
    ? "rgba(15,23,42,0.85)" // slate-900
    : "rgba(148,163,184,0.95)"; // slate-400

  const subtitleTone = highContrast ? "text-slate-700" : "text-slate-500";
  const chipTone = highContrast ? "text-slate-700" : "text-slate-500";
  const chipBorder = highContrast ? "border-slate-300" : "border-slate-200";
  const chipBg = highContrast ? "bg-slate-100" : "bg-slate-50";
  const btnBorder = highContrast ? "border-slate-300" : "border-slate-200";
  const btnBg = highContrast ? "bg-white" : "bg-slate-50";
  const btnHover = highContrast ? "hover:bg-slate-50" : "hover:bg-slate-100";

  // Metrics
  const emailMetrics = useChannelMetrics(data, CHANNELS.EMAIL);
  const liMetrics = useChannelMetrics(data, CHANNELS.LINKEDIN);
  const current = channel === CHANNELS.EMAIL ? emailMetrics : liMetrics;

  const channelBadge = channel === CHANNELS.EMAIL ? "Email" : "LinkedIn";

  // Interested stats (COMMON)
  const interestedCount = Number(data?.stats?.interested?.count ?? 0) || 0;
  const interestedRatePct = (() => {
    const r = data?.stats?.interested?.rate;
    return Number.isFinite(r) ? Number(r) * 100 : 0;
  })();

  return (
    <div>
      <HeaderMainPage />
      <div className="min-h-screen w-full bg-slate-50 bg-[radial-gradient(1200px_600px_at_10%_-10%,rgba(129,140,248,0.18),transparent),radial-gradient(900px_400px_at_90%_10%,rgba(56,189,248,0.14),transparent)] px-4 py-6 md:px-8">
        <div className="mx-auto max-w-7xl">
          {/* Header */}
          <div className="mb-6 flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
            <div>
              <h1 className="text-2xl font-semibold tracking-tight opacity-1 bg-gradient-to-r from-gray-700 via-blue-700 to-fuchsia-700  bg-clip-text text-transparent md:text-3xl">
                Outreach Dashboard
              </h1>
              <p className={`mt-1 text-sm ${subtitleTone}`}>
                Snapshot of campaigns, companies and engagement across your
                multichannel outreach.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {/* Channel toggle */}
              <div
                className={`flex items-center rounded-full border ${btnBorder} ${btnBg} p-1 text-xs font-medium shadow-sm`}
              >
                {Object.values(CHANNELS).map((c) => (
                  <button
                    key={c}
                    onClick={() => setChannel(c)}
                    className={`rounded-full px-3 py-1.5 transition ${
                      channel === c
                        ? "bg-slate-900 text-slate-50"
                        : "text-slate-600 hover:bg-slate-100"
                    }`}
                  >
                    {c === CHANNELS.EMAIL ? "Email" : "LinkedIn"}
                  </button>
                ))}
              </div>
              {/* Contrast & Refresh */}
              <button
                onClick={() => setHighContrast((v) => !v)}
                className={`inline-flex items-center gap-2 rounded-xl border ${btnBorder} ${btnBg} px-3 py-2 text-xs font-medium text-slate-600 shadow-sm ${btnHover}`}
                title="Toggle contrast"
              >
                Contrast: {highContrast ? "High" : "Normal"}
              </button>
              <button
                onClick={load}
                className={`inline-flex items-center gap-2 rounded-xl border ${btnBorder} ${btnBg} px-4 py-2 text-sm font-medium text-slate-700 shadow-sm ${btnHover}`}
              >
                <RefreshCw className="h-4 w-4" />
                Refresh
              </button>
            </div>
          </div>

          {/* Status/Error Strip */}
          <div className="mb-6 flex flex-wrap items-center gap-3 text-xs">
            {lastUpdated ? (
              <span
                className={`rounded-full border ${chipBorder} ${chipBg} px-3 py-1 ${chipTone}`}
              >
                Last updated: {lastUpdated}
              </span>
            ) : null}
            <span
              className={`rounded-full border ${chipBorder} ${chipBg} px-3 py-1 ${chipTone}`}
            >
              Channel: {channelBadge}
            </span>
            {usingMock ? (
              <span className="rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-amber-700">
                Using mock data (API unreachable)
              </span>
            ) : (
              <span className="rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-emerald-700">
                Live data
              </span>
            )}
            {error ? (
              <span className="rounded-full border border-rose-200 bg-rose-50 px-3 py-1 text-rose-700">
                {error}
              </span>
            ) : null}
          </div>

          {/* KPI Cards */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {loading ? (
              <>
                <Skeleton className="h-28" />
                <Skeleton className="h-28" />
                <Skeleton className="h-28" />
                <Skeleton className="h-28" />
              </>
            ) : (
              <>
                <StatCard
                  hc={highContrast}
                  icon={Rocket}
                  label="Active Campaigns"
                  value={data?.num_of_campaigns ?? 0}
                  hint="Running or scheduled campaigns"
                  accent="from-fuchsia-50 via-white to-violet-50"
                />
                <StatCard
                  hc={highContrast}
                  icon={Mail}
                  label="Total Companies"
                  value={data?.num_of_companies ?? 0}
                  hint="Unique accounts under outreach"
                  accent="from-sky-50 via-white to-cyan-50"
                />
                <StatCard
                  hc={highContrast}
                  icon={BarChart3}
                  label="Open Rate"
                  badge={channelBadge}
                  value={`${current.openRate.toFixed(1)}%`}
                  hint={`${current.totals.opened} of ${Math.max(
                    current.totals.sent,
                    current.totals.total
                  )} opened`}
                  accent="from-violet-50 via-white to-indigo-50"
                />
                <StatCard
                  hc={highContrast}
                  icon={CheckCircle2}
                  label="Reply Rate"
                  badge={channelBadge}
                  value={`${current.replyRate.toFixed(1)}%`}
                  hint={`${
                    current.totals.replied +
                    (channel === CHANNELS.LINKEDIN
                      ? current.totals.responded
                      : 0)
                  } replies so far`}
                  accent="from-emerald-50 via-white to-teal-50"
                />
              </>
            )}
          </div>

          {/* Charts + Breakdown */}
          <div className="mt-6 flex flex-col gap-6 lg:flex-row">
            {/* Status Distribution */}
            <div className="flex w-full lg:w-5/12">
              <Section
                hc={highContrast}
                title={`Status Distribution — ${channelBadge}`}
                icon={PieChart}
                actions={
                  <div className="flex flex-col items-end text-xs">
                    <span
                      className={`${
                        highContrast ? "text-slate-600" : "text-slate-500"
                      }`}
                    >
                      Across all companies
                    </span>
                    {!loading && (
                      <span className="text-slate-500">
                        {current.totals.total} total companies
                      </span>
                    )}
                  </div>
                }
              >
                {loading ? (
                  <Skeleton className="h-[18rem]" />
                ) : (
                  <div className="flex flex-col gap-5 md:flex-row">
                    {/* Donut */}
                    <div className="w-full md:w-3/5">
                      <div className="relative h-[18rem] rounded-2xl bg-white/80 p-3 ring-1 ring-slate-200">
                        <ResponsiveContainer width="100%" height="100%">
                          <RPieChart>
                            <Pie
                              dataKey="count"
                              nameKey="name"
                              data={current.list}
                              cx="50%"
                              cy="50%"
                              innerRadius={60}
                              outerRadius={90}
                              paddingAngle={2}
                              labelLine={false}
                              label={({
                                cx,
                                cy,
                                midAngle,
                                innerRadius,
                                outerRadius,
                                percent,
                              }) => {
                                if (!percent || Number.isNaN(percent)) return null;
                                const RADIAN = Math.PI / 180;
                                const radius =
                                  innerRadius +
                                  (outerRadius - innerRadius) * 0.55;
                                const x =
                                  cx + radius * Math.cos(-midAngle * RADIAN);
                                const y =
                                  cy + radius * Math.sin(-midAngle * RADIAN);

                                if (percent * 100 < 4) return null;

                                return (
                                  <text
                                    x={x}
                                    y={y}
                                    fill="#0f172a"
                                    textAnchor="middle"
                                    dominantBaseline="central"
                                    style={{ fontSize: 10, fontWeight: 600 }}
                                  >
                                    {(percent * 100).toFixed(0)}%
                                  </text>
                                );
                              }}
                            >
                              {current.list.map((s) => (
                                <Cell key={s.status} fill={s.color} />
                              ))}
                            </Pie>
                          </RPieChart>
                        </ResponsiveContainer>

                        {/* Center */}
                        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                          <div className="flex h-28 w-28 flex-col items-center justify-center rounded-full bg-white/95 text-center shadow-sm ring-1 ring-slate-200">
                            <p className="text-[9px] font-semibold uppercase tracking-[0.18em] text-slate-400">
                              Distribution
                            </p>
                            <p className="mt-0.5 text-[11px] font-medium text-slate-900">
                              {channelBadge}
                            </p>
                            <p className="mt-0.5 text-[10px] text-slate-500 leading-tight">
                              {current.totals.total} companies
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Legend */}
                    <div className="w-full rounded-2xl bg-gradient-to-b from-slate-50 to-white p-4 ring-1 ring-slate-200 md:w-2/5">
                      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                        Status mix
                      </p>
                      <p className="mt-1 text-xs text-slate-500">
                        How companies are distributed across individual stages.
                      </p>
                      <ul className="mt-3 space-y-2.5">
                        {current.list.map((s) => (
                          <li
                            key={s.status}
                            className="flex items-center justify-between gap-3 rounded-xl bg-white/80 px-3 py-2 ring-1 ring-slate-100"
                          >
                            <div className="flex items-center gap-2">
                              <span
                                className="h-2.5 w-2.5 rounded-full"
                                style={{ backgroundColor: s.color }}
                              />
                              <span className="text-xs font-medium capitalize text-slate-800">
                                {s.status}
                              </span>
                            </div>
                            <div className="flex items-center gap-2 text-xs">
                              <span className="font-semibold text-slate-900">
                                {s.count}
                              </span>
                              {current.totals.total > 0 && (
                                <span className="text-slate-500">
                                  {((s.count / current.totals.total) * 100).toFixed(1)}%
                                </span>
                              )}
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}
              </Section>
            </div>

            {/* Engagement Funnel */}
            <div className="flex w-full lg:w-7/12">
              <Section
                hc={highContrast}
                title={`Engagement Funnel — ${channelBadge}`}
                icon={ShieldCheck}
              >
                {loading ? (
                  <div className="grid gap-3 md:grid-cols-2">
                    <Skeleton className="h-24" />
                    <Skeleton className="h-24" />
                    <Skeleton className="h-10 md:col-span-2" />
                  </div>
                ) : (
                  <div className="flex flex-col gap-6 md:flex-row">
                    {/* Left */}
                    <div className="w-full space-y-4 rounded-2xl bg-gradient-to-br from-slate-50 via-white to-indigo-50 p-4 ring-1 ring-slate-200 md:w-2/3">
                      <div className="flex items-center justify-between gap-2">
                        <div>
                          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                            Conversion journey
                          </p>
                          <p className="text-xs text-slate-500">
                            How {channelBadge.toLowerCase()} move from sent to opened and replied.
                          </p>
                        </div>
                        <div className="flex flex-col items-end text-xs text-slate-500">
                          <span className="font-semibold text-slate-800">
                            {current.totals.sent} sent
                          </span>
                          <span>
                            {current.totals.opened} opened •{" "}
                            {current.totals.replied +
                              (channel === CHANNELS.LINKEDIN ? current.totals.responded : 0)}{" "}
                            replied
                          </span>
                        </div>
                      </div>

                      <div className="space-y-4">
                        <ProgressStat hc={highContrast} label="Open rate" value={current.openRate} />
                        <ProgressStat hc={highContrast} label="Reply rate" value={current.replyRate} />
                      </div>

                      {/* Chips row (Interested is COMMON) */}
                      <div className="mt-2 flex flex-wrap gap-2 text-[11px]">
                        <div className="inline-flex items-center gap-2 rounded-full bg-emerald-50 px-3 py-1 ring-1 ring-emerald-100">
                          <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                          <span className="font-medium text-emerald-800">Quality responses</span>
                          <span className="text-emerald-700">
                            {current.totals.replied +
                              (channel === CHANNELS.LINKEDIN ? current.totals.responded : 0)}{" "}
                            replies
                          </span>
                        </div>

                        <div className="inline-flex items-center gap-2 rounded-full bg-indigo-50 px-3 py-1 ring-1 ring-indigo-100">
                          <span className="h-1.5 w-1.5 rounded-full bg-indigo-500" />
                          <span className="font-medium text-indigo-800">Top of funnel</span>
                          <span className="text-indigo-700">{current.totals.opened} opens</span>
                        </div>

                        <div className="inline-flex items-center gap-2 rounded-full bg-slate-50 px-3 py-1 ring-1 ring-slate-200">
                          <span className="h-1.5 w-1.5 rounded-full bg-slate-400" />
                          <span className="font-medium text-slate-800">Total reach</span>
                          <span className="text-slate-600">{current.totals.total} companies</span>
                        </div>

                        <div className="inline-flex items-center gap-2 rounded-full bg-amber-50 px-3 py-1 ring-1 ring-amber-100">
                          <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
                          <span className="font-medium text-amber-800">Interested</span>
                          <span className="text-amber-700">
                            {interestedCount} • {interestedRatePct.toFixed(1)}%
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Right */}
                    <div className="w-full rounded-2xl border border-slate-200 bg-white p-4 md:w-1/3">
                      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                        Funnel insight
                      </p>
                      <p className="mt-2 text-sm font-medium text-slate-900">
                        {current.replyRate.toFixed(1)}% reply rate from {channelBadge.toLowerCase()}.
                      </p>
                      <p className="mt-1 text-xs text-slate-500">
                        Opens and replies are calculated relative to{" "}
                        <span className="font-semibold text-slate-900">sent</span>{" "}
                        {channelBadge.toLowerCase()} whenever possible.
                      </p>

                      {/* Interested insight (COMMON) */}
                      <p className="mt-2 text-xs text-slate-600">
                        <span className="font-semibold text-slate-900">Interested:</span>{" "}
                        {interestedCount} companies ({interestedRatePct.toFixed(1)}% of prospects).
                      </p>

                      <div className="mt-4 space-y-2 text-xs text-slate-600">
                        <div className="flex items-start gap-2">
                          <span className="mt-[3px] h-1.5 w-1.5 rounded-full bg-indigo-500" />
                          <p>
                            <span className="font-semibold">Open rate</span> shows how many contacts are engaging with your first touch.
                          </p>
                        </div>
                        <div className="flex items-start gap-2">
                          <span className="mt-[3px] h-1.5 w-1.5 rounded-full bg-emerald-500" />
                          <p>
                            <span className="font-semibold">Reply rate</span> tracks true conversations created from those touches.
                          </p>
                        </div>
                        <div className="flex items-start gap-2">
                          <span className="mt-[3px] h-1.5 w-1.5 rounded-full bg-slate-400" />
                          <p>
                            Use this view to benchmark different{" "}
                            <span className="font-semibold">campaigns, subjects</span> and{" "}
                            <span className="font-semibold">engagement styles</span>.
                          </p>
                        </div>
                      </div>

                      <div
                        className={`mt-4 rounded-xl bg-slate-50 px-3 py-2 text-[11px] ${
                          highContrast ? "text-slate-600" : "text-slate-500"
                        }`}
                      >
                        {channel === CHANNELS.EMAIL ? (
                          <>
                            Calculated relative to{" "}
                            <span className="font-semibold text-slate-900">sent (email)</span>.
                          </>
                        ) : (
                          <>
                            Replies include{" "}
                            <span className="font-semibold text-slate-900">replied + responded</span>.
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </Section>
            </div>
          </div>

          {/* Status by Stage */}
          <div className="mt-6">
            <Section hc={highContrast} title={`Status by Stage — ${channelBadge}`} icon={BarChart3}>
              {loading ? (
                <Skeleton className="h-80" />
              ) : (
                <div className="flex flex-col gap-6 lg:flex-row">
                  <div className="w-full space-y-4 lg:w-2/3">
                    <div className="flex flex-wrap gap-3 text-xs">
                      <div className="flex items-center gap-2 rounded-xl bg-slate-50 px-3 py-2 ring-1 ring-slate-200">
                        <span className="h-2 w-2 rounded-full bg-slate-400" />
                        <span className="font-medium text-slate-800">Total</span>
                        <span className="text-slate-500">{current.totals.total} companies</span>
                      </div>
                      <div className="flex items-center gap-2 rounded-xl bg-emerald-50 px-3 py-2 ring-1 ring-emerald-100">
                        <span className="h-2 w-2 rounded-full bg-emerald-500" />
                        <span className="font-medium text-emerald-800">Engaged</span>
                        <span className="text-emerald-700">
                          {current.totals.opened +
                            current.totals.replied +
                            (channel === CHANNELS.LINKEDIN ? current.totals.responded : 0)}{" "}
                          touchpoints
                        </span>
                      </div>
                      <div className="flex items-center gap-2 rounded-xl bg-indigo-50 px-3 py-2 ring-1 ring-indigo-100">
                        <span className="h-2 w-2 rounded-full bg-indigo-500" />
                        <span className="font-medium text-indigo-800">Sent</span>
                        <span className="text-indigo-700">
                          {current.totals.sent} {channelBadge.toLowerCase()} sent
                        </span>
                      </div>
                    </div>

                    <div className="h-72 rounded-2xl bg-white/70 p-3 ring-1 ring-slate-200">
                      <ResponsiveContainer width="100%" height="100%">
                        <RBarChart data={current.list} barCategoryGap={18}>
                          <CartesianGrid
                            strokeDasharray="3 3"
                            stroke="rgba(148,163,184,0.3)"
                            vertical={false}
                          />
                          <XAxis
                            dataKey="status"
                            stroke={axisColor}
                            tick={{ fontSize: 11, fill: axisColor }}
                            tickLine={false}
                            axisLine={{ stroke: axisColor, strokeWidth: 0.8 }}
                          />
                          <YAxis
                            stroke={axisColor}
                            tick={{ fontSize: 11, fill: axisColor }}
                            tickLine={false}
                            axisLine={{ stroke: axisColor, strokeWidth: 0.8 }}
                          />
                          <RTooltip
                            cursor={{ fill: "rgba(148,163,184,0.08)" }}
                            contentStyle={{
                              background: "#ffffff",
                              border: "1px solid rgba(148,163,184,0.4)",
                              borderRadius: "0.75rem",
                              boxShadow: "0 18px 45px -20px rgba(15,23,42,0.25)",
                              color: "#0f172a",
                              fontSize: "12px",
                            }}
                            labelStyle={{
                              fontWeight: 600,
                              marginBottom: 4,
                              color: "#0f172a",
                            }}
                          />
                          <Bar dataKey="count" radius={[8, 8, 0, 0]}>
                            {current.list.map((s) => (
                              <Cell key={s.status} fill={s.color} />
                            ))}
                          </Bar>
                        </RBarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>

                  <div className="w-full rounded-2xl border border-slate-200 bg-gradient-to-b from-slate-50 to-white p-4 lg:w-1/3">
                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                      Stage breakdown
                    </p>
                    <p className="mt-1 text-xs text-slate-500">
                      Quick view of how companies are distributed across stages for{" "}
                      <span className="font-medium text-slate-800">{channelBadge}</span>.
                    </p>
                    <ul className="mt-4 space-y-2.5">
                      {current.list.map((s) => (
                        <li
                          key={s.status}
                          className="flex items-center justify-between gap-3 rounded-xl bg-white/80 px-3 py-2 ring-1 ring-slate-100"
                        >
                          <div className="flex items-center gap-2">
                            <span
                              className="h-2.5 w-2.5 rounded-full"
                              style={{ backgroundColor: s.color }}
                            />
                            <span className="text-xs font-medium capitalize text-slate-800">
                              {s.status}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 text-xs">
                            <span className="font-semibold text-slate-900">{s.count}</span>
                            {current.totals.total > 0 && (
                              <span className="text-slate-500">
                                {((s.count / current.totals.total) * 100).toFixed(1)}%
                              </span>
                            )}
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
            </Section>
          </div>
        </div>
      </div>
    </div>
  );
}
